import { createAsyncThunk } from "@reduxjs/toolkit";
import { validateFields, closeRequestContactUpdateDialog, openSuccessDialog } from "@requestContactUpdate/store/ducks";
import { requestContactUpdateSelector } from "@requestContactUpdate/store/selectors";
import { openNotificationThunk } from "@store/Notification/thunks";
import { ThunkApiConfig } from "@shared/models/redux";

export interface RequestContactUpdateBody {
  requestType: string;
  request: string;
  entitledAccounts: string[];
  entitledEntities: string[];
  attachmentName?: string;
  attachment?: string;
  customFields?: { [key: string]: any };
}

export const sendRequestContactUpdateThunk = createAsyncThunk<boolean, RequestContactUpdateBody, ThunkApiConfig>(
  "requestContactUpdate/sendRequestContactUpdate",
  async (body, { rejectWithValue, dispatch, extra }) => {
    try {
      // LPC-10934: now creates a Salesforce Case directly via contact-service-api (with
      // contactManagedBy queue routing), replacing the old S3-pickup path on entitlement-svc.
      const response = await extra<{ caseId: string }, RequestContactUpdateBody>(
        "contacts/v1/user/RequestContactUpdate",
        { method: "post", body }
      );

      if (response.data?.caseId) {
        dispatch(closeRequestContactUpdateDialog());
        dispatch(openSuccessDialog());

        return true;
      }

      return false;
    } catch (error: any) {
      const errorMessage = error.response?.data?.[0] || "Some error occurred. Please try again.";

      dispatch(
        openNotificationThunk({
          variant: "warning",
          text: errorMessage,
        })
      );

      return rejectWithValue(errorMessage);
    }
  }
);

export const submitFormThunk = createAsyncThunk<void, void, ThunkApiConfig>(
  "requestContactUpdate/submitFormThunk",
  // LPC-10991: Must be async + await the inner network thunk.
  // Previously this thunk returned synchronously after dispatching
  // sendRequestContactUpdateThunk, which caused submitFormThunk/fulfilled to
  // fire in the next microtask — flipping slice `loading` back to false WHILE
  // the network call was still in flight. The Submit button then re-enabled
  // and accepted further clicks, dispatching duplicate Salesforce case-create
  // requests. Awaiting the inner dispatch keeps `loading=true` for the entire
  // submit lifecycle, matching the InvestorRequest pattern.
  async (_, { dispatch, getState }) => {
    dispatch(validateFields());

    const { requestType, commonFields, customFields, validationWarningShown } = requestContactUpdateSelector(
      getState()
    );

    if (validationWarningShown) return;

    const fallbackMessage = "Request Contact Update - Self Service";

    const message =
      commonFields.message && commonFields.message.trim().length > 0 ? commonFields.message : fallbackMessage;

    const contactName = (commonFields.contactName || "").trim();
    const contactEmail = (commonFields.contactEmail || "").trim();
    /** LPC-10619 description line: "Request for Changes To: [Name], [Email]" */
    const RequestForChangesTo = `: ${contactName}, ${contactEmail}`;

    const body: RequestContactUpdateBody = {
      customFields: {
        ...customFields,
        RequestForChangesTo,
      },
      requestType: requestType || "",
      request: message,
      entitledAccounts: commonFields.selectedAccounts,
      entitledEntities: commonFields.selectedFunds,
    };

    if (commonFields.attachment) {
      body.attachmentName = commonFields.attachment.name;
      body.attachment = commonFields.attachment.base64;
    }

    await dispatch(sendRequestContactUpdateThunk(body));
  }
);
